import joinUrl from 'url-join';
import ajaxPromises from 'common/js/AjaxPromises';
import globalConfig from 'common/js/config';
import { generateUrlWithRegion } from 'common/js/url-utils';
import { CreativeTagTrackingPayload, PayloadDatas } from '../../types';
import { CreativeTagEvent } from '../../enums';
import {
  isComplexPayloadResult,
  PayloadBuilderStrategy,
  SingleResult
} from '../../payload-builder-strategies/payload-builder-strategy';

export class CreativeTrackingService {
  private readonly baseUrl = joinUrl(
    generateUrlWithRegion(globalConfig.MS_ADS_MONITORING_EVENTS_URL),
    'ocs'
  );

  private allreadySentEvents: {
    [key in CreativeTagEvent]?: boolean;
  } = {
    [CreativeTagEvent.CreativeLoadingFailed]: false,
    // Meta DSP emits this from two sources (RuntimeErrorObserver +
    // ResourceLoadObserver); dedup so at most one pixel fires per creative.
    [CreativeTagEvent.CreativeRenderingError]: false,
    // Display is a once-per-creative viewability milestone (the viewable-rate
    // numerator). The engine only guards it per-delegate
    // (`perDelegateMetrics[delegate.id].displayed`), which resets when a
    // skin-video's element churn removes/re-installs a delegate — so the engine
    // legitimately re-dispatches Display and the rich bridge forwards each one.
    // Dedup here so at most one Display pixel fires per creative (ADV-30177-4).
    [CreativeTagEvent.CreativeDisplay]: false
  };

  constructor(
    private readonly payloadBuilderStrategy: PayloadBuilderStrategy
  ) {}

  async sendCreativeTagTracking(
    creativeTagEvent: CreativeTagEvent,
    payload?: PayloadDatas,
    evt?: CustomEvent
  ): Promise<void> {
    if (
      this.allreadySentEvents[creativeTagEvent] !== undefined &&
      this.allreadySentEvents[creativeTagEvent]
    ) {
      return;
    }

    const eventPayload = this.payloadBuilderStrategy.buildPayload(
      creativeTagEvent,
      payload,
      evt
    );

    // The strategy returns `null` for events it does not track (e.g. meta-dsp
    // drops everything outside its 3-tracker contract, or when its enriched
    // context is missing). Skip silently — otherwise an empty request would be
    // fired against the default endpoint.
    if (!eventPayload) {
      return;
    }

    if (this.allreadySentEvents[creativeTagEvent] !== undefined) {
      this.allreadySentEvents[creativeTagEvent] = true;
    }

    if (Array.isArray(eventPayload)) {
      await Promise.all(eventPayload.map(this.sendPayload.bind(this)));
    } else {
      await this.sendPayload(eventPayload);
    }
  }

  protected async sendPayload(eventPayload: SingleResult): Promise<void> {
    let baseUrl = this.baseUrl;
    let method: 'GET' | 'POST' = 'GET';
    if (isComplexPayloadResult(eventPayload)) {
      baseUrl = eventPayload.endPoint.url;
      method = eventPayload.endPoint.method;
      eventPayload = eventPayload.payload;
    }

    if (method === 'GET') {
      await this.sendGetRequest(baseUrl, eventPayload);
    } else {
      await this.sendPostRequest(baseUrl, eventPayload);
    }
  }

  protected sendPostRequest(
    baseUrl: string,
    eventPayload: CreativeTagTrackingPayload
  ): Promise<void> {
    return ajaxPromises.postJson(baseUrl, eventPayload).catch(console.error);
  }

  protected sendGetRequest(
    baseUrl: string,
    eventPayload: CreativeTagTrackingPayload
  ): Promise<void> {
    const queryParts: string[] = [];
    Object.entries(eventPayload).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        let strValue: string;
        if (typeof value === 'object' && !Array.isArray(value)) {
          strValue = encodeURIComponent(JSON.stringify(value));
        } else {
          strValue = encodeURIComponent(String(value));
        }
        queryParts.push(`${encodeURIComponent(key)}=${strValue}`);
      }
    });
    const urlWithParams = `${baseUrl}?${queryParts.join('&')}`;

    return ajaxPromises.get(urlWithParams).catch(console.error);
  }
}
